import React from 'react';
import { DashboardCard } from './DashboardLayout';
import { Star, Clock, Users, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../ui/utils';

interface Recipe {
  id: string;
  title: string;
  image?: string;
  rating?: number;
  cookTime?: number;
  servings?: number;
  saved?: boolean;
}

interface RecipeRecommendationsProps {
  recipes?: Recipe[];
  onRecipeClick?: (recipeId: string) => void;
  onSaveRecipe?: (recipeId: string) => void;
  className?: string;
}

export function RecipeRecommendations({
  recipes = [],
  onRecipeClick,
  onSaveRecipe,
  className
}: RecipeRecommendationsProps) {
  const hasRecipes = recipes.length > 0;

  return (
    <div className={cn('col-span-1 md:col-span-8 lg:col-span-12', className)}>
      <DashboardCard className="!col-span-1">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-dusk">Recommended for You</h2>
          <Button
            variant="ghost"
            size="sm"
            className="text-coral hover:text-coral/80"
            onClick={() => onRecipeClick?.('discover')}
          >
            View All →
          </Button>
        </div>

        {!hasRecipes ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 rounded-full bg-neutral-100 p-4">
              <Star className="h-8 w-8 text-supporting" />
            </div>
            <p className="text-sm text-supporting">
              No recommendations yet
            </p>
            <p className="mt-1 text-xs text-supporting">
              Complete your profile to get personalized suggestions
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recipes.map((recipe) => (
              <div
                key={recipe.id}
                className="group cursor-pointer overflow-hidden rounded-lg border border-neutral-200 transition-shadow hover:shadow-md"
                onClick={() => onRecipeClick?.(recipe.id)}
              >
                {/* Recipe Image */}
                <div className="relative aspect-video overflow-hidden bg-neutral-100">
                  {recipe.image ? (
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Star className="h-12 w-12 text-neutral-300" />
                    </div>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveRecipe?.(recipe.id);
                    }}
                    className={cn(
                      'absolute right-2 top-2 rounded-full p-2 backdrop-blur-sm transition-colors',
                      recipe.saved
                        ? 'bg-coral text-white'
                        : 'bg-white/80 text-coral hover:bg-white'
                    )}
                  >
                    <Heart className={cn(
                      'h-4 w-4',
                      recipe.saved && 'fill-current'
                    )} />
                  </button>
                </div>

                {/* Recipe Info */}
                <div className="p-4">
                  <h3 className="mb-2 line-clamp-2 font-semibold text-dusk">
                    {recipe.title}
                  </h3>

                  <div className="flex items-center gap-4 text-xs text-supporting">
                    {recipe.rating && (
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        <span>{recipe.rating.toFixed(1)}</span>
                      </div>
                    )}

                    {recipe.cookTime && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{recipe.cookTime}m</span>
                      </div>
                    )}

                    {recipe.servings && (
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{recipe.servings}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DashboardCard>
    </div>
  );
}
